import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  CheckCircle, 
  Circle, 
  Clock, 
  BookOpen,
  Code,
  Type,
  Download,
  ExternalLink,
  Menu,
  X,
  Maximize,
  Minimize,
  Info,
  FolderTree
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTutorial } from '../hooks/useApi';
import { usePlatformTranslation } from '../hooks/usePlatformTranslation';
import { apiService } from '../services/api';
import { keywordService } from '../services/keywordService';
import glossaryService from '../services/glossaryService';
import { Tutorial, TutorialStep, LearningCollection } from '../types';
import { cn } from '../utils/cn';
import { marked } from 'marked';
import GlobalAudioPlayer from '../components/GlobalAudioPlayer';
import HighlightableText from '../components/HighlightableText';
import TechnicalTermsSidebar from '../components/TechnicalTermsSidebar';
import LanguageSwitcher from '../components/LanguageSwitcher';
import TranslationProgress from '../components/TranslationProgress';
import { useLanguage } from '../contexts/LanguageContext';

// Configure marked for better rendering
marked.setOptions({
  breaks: true,
  gfm: true,
});

// Helper to render markdown to HTML with stable string typing
const renderMarkdown = (markdown: string): string => {
  try {
    const anyMarked: any = marked as any;
    let html: string;
    if (typeof anyMarked.parse === 'function') {
      html = anyMarked.parse(markdown) as string;
    } else {
      html = (anyMarked(markdown) as unknown as string) || '';
    }
    
    // Convert relative image URLs to absolute URLs using backend API base URL
    // CRITICAL: Always use backend API URL (kb-api.dev.viaphoton.dev), never frontend URL
    let imageBaseUrl = import.meta.env.VITE_VIAPHOTON_API_URL;
    
    if (imageBaseUrl) {
      // Remove /api suffix if present since images are served from /uploads/ not /api/uploads/
      imageBaseUrl = imageBaseUrl.replace(/\/api\/?$/, '');
    } else if (typeof window !== 'undefined') {
      // In development, use localhost:3001
      if (import.meta.env.DEV || window.location.hostname === 'localhost') {
        imageBaseUrl = 'http://localhost:3001';
      } else {
        // In production, construct backend API URL from frontend URL
        // kb.dev.viaphoton.dev -> kb-api.dev.viaphoton.dev
        // kb.viaphoton.dev -> kb-api.viaphoton.dev
        const hostname = window.location.hostname;
        const protocol = window.location.protocol;
        
        if (hostname.includes('kb.dev.viaphoton.dev') && !hostname.includes('kb-api')) {
          imageBaseUrl = `${protocol}//${hostname.replace('kb.dev.viaphoton.dev', 'kb-api.dev.viaphoton.dev')}`;
        } else if (hostname.includes('kb.viaphoton.dev') && !hostname.includes('kb-api')) {
          imageBaseUrl = `${protocol}//${hostname.replace('kb.viaphoton.dev', 'kb-api.viaphoton.dev')}`;
        } else if (hostname.includes('kb-api')) {
          // Already the API domain, use as-is
          imageBaseUrl = `${protocol}//${hostname}`;
        } else {
          // Fallback: try to construct API domain
          imageBaseUrl = `${protocol}//kb-api.${hostname.replace(/^[^.]+\./, '')}`;
        }
      }
    }
    
    if (imageBaseUrl) {
      // Replace relative /uploads/ paths with absolute backend API URLs
      // Also handle markdown image syntax ![alt](url)
      html = html.replace(
        /src="(\/uploads\/[^"]+)"/g,
        (_match, path) => {
          return `src="${imageBaseUrl}${path}"`;
        }
      );
      // Also fix markdown image syntax
      html = html.replace(
        /!\[([^\]]*)\]\((\/uploads\/[^)]+)\)/g,
        (_match, alt, path) => {
          return `![${alt}](${imageBaseUrl}${path})`;
        }
      );
    }
    
    return html;
  } catch {
    return markdown;
  }
};

interface TutorialProgress {
  tutorialId: string;
  currentStepIndex: number;
  completedSteps: number[];
  quizScores: { [stepId: string]: number };
  startTime: number;
  lastAccessed: number;
}

interface QuizState {
  currentQuestion: number;
  answers: number[];
  score: number;
  isCompleted: boolean;
}

// Component to convert SharePoint webUrl to proxy URL and embed video
const SharePointVideoConverter: React.FC<{ webUrl: string; title: string; sharePointUrl: string }> = ({ webUrl, title, sharePointUrl }) => {
  const [proxyUrl, setProxyUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const convertWebUrl = async () => {
      try {
        setLoading(true);
        setError(false);
        const response = await fetch(`/api/ai-assistant/sharepoint/convert-url?webUrl=${encodeURIComponent(webUrl)}`);
        const data = await response.json();
        
        if (data.success && data.proxyUrl) {
          setProxyUrl(data.proxyUrl);
        } else {
          setError(true);
        }
      } catch (err) {
        console.error('Failed to convert SharePoint webUrl:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    convertWebUrl();
  }, [webUrl]);

  if (loading) {
    return (
      <div className="relative w-full h-96 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading video...</p>
        </div>
      </div>
    );
  }

  if (error || !proxyUrl) {
    // Fallback: show link button
    return (
      <div className="relative w-full h-96 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg overflow-hidden border-2 border-blue-200 flex items-center justify-center">
        <div className="text-center space-y-4 p-8">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
            <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Video Available on SharePoint</h3>
            <p className="text-gray-600 mb-4">This video is stored on SharePoint. Click below to watch it.</p>
            <a
              href={sharePointUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Watch Video on SharePoint</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Success: embed video using proxy URL
  return (
    <div className="relative w-full bg-gray-100 rounded-lg overflow-hidden">
      <video 
        controls 
        className="w-full h-auto max-h-[600px]"
        style={{ maxWidth: '100%' }}
      >
        <source src={proxyUrl} type="video/mp4" />
        Your browser does not support the video tag.
        <a href={sharePointUrl} target="_blank" rel="noopener noreferrer">
          Watch on SharePoint
        </a>
      </video>
      <div className="mt-2 text-center">
        <a
          href={sharePointUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800"
        >
          <span>Open in SharePoint</span>
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
};

const TutorialViewer: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [tutorial, setTutorial] = useState<Tutorial | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [progress, setProgress] = useState<TutorialProgress | null>(null);
  const [quizStates, setQuizStates] = useState<{ [stepId: string]: QuizState }>({});
  const [activeTranslation, setActiveTranslation] = useState<string | null>(null);
  const [isTechnicalTermsOpen, setIsTechnicalTermsOpen] = useState(false);
  const [isHighlightingEnabled, setIsHighlightingEnabled] = useState(true);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true); // Navigation menu collapsed by default
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [hasUserNavigated, setHasUserNavigated] = useState(false);
  const [shouldAutoPlay, setShouldAutoPlay] = useState(false);
  const [isStepsCollapsed, setIsStepsCollapsed] = useState(false); // Steps sidebar open by default
  const [autoCompleteProgress, setAutoCompleteProgress] = useState<number>(0);
  const [calculatedTime, setCalculatedTime] = useState<number>(0);
  const [collections, setCollections] = useState<LearningCollection[]>([]);
  
  const { currentLanguage } = useLanguage();

  // Ensure glossary is loaded for keyword highlighting
  useEffect(() => {
    glossaryService.refreshFromAPI().catch((error) => {
      console.error('Failed to refresh glossary in TutorialViewer:', error);
    });
  }, []);

  // Reset auto-play flag after it's used
  useEffect(() => {
    if (shouldAutoPlay) {
      const timer = setTimeout(() => {
        setShouldAutoPlay(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [shouldAutoPlay]);
  const { t } = usePlatformTranslation();

  // Initialize keyword service
  useEffect(() => {
    console.log('TutorialViewer: Initializing keyword service');
    keywordService.refreshFromGlossary();
  }, []);

  // Fetch individual tutorial from API
  const { data: tutorialData, loading, error } = useTutorial(id || '');

  // Reset highlighted keywords when tutorial changes
  useEffect(() => {
    // Clear highlighted keywords when tutorial ID changes
    if (id && (window as any).__resetHighlightedKeywords) {
      (window as any).__resetHighlightedKeywords();
    }
  }, [id]);

  // Load collections containing this tutorial
  useEffect(() => {
    if (id) {
      const loadCollections = async () => {
        try {
          const response = await apiService.getCollectionsByTutorial(id);
          if (response.success && response.data) {
            setCollections(response.data);
          }
        } catch (error) {
          console.error('Error loading collections:', error);
        }
      };
      loadCollections();
    }
  }, [id]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't interfere with input fields
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      // Disable step navigation in continuous scroll mode
      if (tutorial?.layout === 'continuous') {
        return;
      }

      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          if (currentStepIndex > 0) {
            navigateToStep(currentStepIndex - 1);
          }
          break;
        case 'ArrowRight':
          event.preventDefault();
          if (tutorial && currentStepIndex < tutorial.steps.length - 1) {
            navigateToStep(currentStepIndex + 1);
          }
          break;
        case 'f':
        case 'F':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            toggleFullscreen();
          }
          break;
        case 'Escape':
          if (isFullscreen) {
            event.preventDefault();
            exitFullscreen();
          }
          break;
        case ' ':
          event.preventDefault();
          // Stop any ongoing speech synthesis
          if (speechSynthesis.speaking) {
            speechSynthesis.cancel();
          }
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentStepIndex, tutorial, isFullscreen]);

  // Fullscreen functions
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const exitFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Update current step index based on scroll position in continuous mode
  useEffect(() => {
    if (!tutorial || tutorial.layout !== 'continuous') {
      return;
    }

    const handleScroll = () => {
      // Find the scrollable container (the main content div)
      const scrollContainer = document.querySelector('.flex-1.overflow-y-auto') || window;
      const scrollTop = scrollContainer === window 
        ? window.scrollY 
        : (scrollContainer as HTMLElement).scrollTop;
      const viewportOffset = 200; // Offset from top of viewport
      
      // Find which step is currently most visible in the viewport
      let closestStepIndex = 0;
      let closestDistance = Infinity;
      
      tutorial.steps.forEach((step, index) => {
        const element = document.getElementById(`step-${step.id}`);
        if (element) {
          const rect = element.getBoundingClientRect();
          const elementTop = scrollTop + rect.top;
          const distance = Math.abs(elementTop - scrollTop - viewportOffset);
          
          // Check if element is in viewport
          if (rect.top <= viewportOffset && rect.bottom >= 0) {
            if (distance < closestDistance) {
              closestDistance = distance;
              closestStepIndex = index;
            }
          }
        }
      });
      
      setCurrentStepIndex(closestStepIndex);
    };

    // Throttle scroll events for better performance
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    // Listen to scroll on both window and the scrollable container
    const scrollContainer = document.querySelector('.flex-1.overflow-y-auto');
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', throttledScroll, { passive: true });
    }
    window.addEventListener('scroll', throttledScroll, { passive: true });
    
    // Also check on mount and after a short delay to ensure DOM is ready
    setTimeout(handleScroll, 100);

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', throttledScroll);
      }
      window.removeEventListener('scroll', throttledScroll);
    };
  }, [tutorial]);

  // Load tutorial and progress
  useEffect(() => {
    if (tutorialData && id) {
      setTutorial(tutorialData);
      loadProgress(id);
    }
  }, [tutorialData, id]);

  // Load tutorial in current language
  useEffect(() => {
    if (id && currentLanguage !== 'en') {
      loadTutorialInLanguage(id, currentLanguage);
    }
  }, [id, currentLanguage]);

  // Calculate reading time based on content
  const calculateReadingTime = (content: string): number => {
    // Average reading speed: 200-250 words per minute
    const wordsPerMinute = 225;
    const wordCount = content.split(/\s+/).length;
    const readingTimeMinutes = wordCount / wordsPerMinute;
    const readingTimeSeconds = Math.max(readingTimeMinutes * 60, 10); // Minimum 10 seconds
    return Math.min(readingTimeSeconds, 120); // Maximum 2 minutes
  };

  // Auto-complete steps based on content-based timing
  useEffect(() => {
    if (progress && tutorial && currentStepIndex >= 0) {
      const step = tutorial.steps[currentStepIndex];
      if (!step) return;
      
      // Skip auto-completion for quizzes and interactive content
      if (step.type === 'quiz' || step.type === 'code') {
        setAutoCompleteProgress(0);
        setCalculatedTime(0);
        return;
      }
      
      const stepStartTime = Date.now();
      const autoCompleteTime = calculateReadingTime(step.content) * 1000; // Convert to milliseconds
      const calculatedTimeSeconds = Math.round(autoCompleteTime / 1000);
      
      // Set the calculated time for display
      setCalculatedTime(calculatedTimeSeconds);
      
      // Reset auto-complete progress when step changes
      setAutoCompleteProgress(0);
      
      const interval = setInterval(async () => {
        const timeSpent = Date.now() - stepStartTime;
        const progressPercentage = Math.min((timeSpent / autoCompleteTime) * 100, 100);
        setAutoCompleteProgress(progressPercentage);
        
        // Debug logging every 5 seconds
        if (Math.floor(timeSpent / 1000) % 5 === 0 && Math.floor(timeSpent / 1000) > 0) {
        }
        
        // Auto-complete if user has spent enough time on the step
        if (timeSpent >= autoCompleteTime) {
          // Read current progress state from localStorage to avoid stale state
          const currentProgressData = JSON.parse(localStorage.getItem(`tutorial-progress-${tutorial.id}`) || '{}');
          const isCurrentlyCompleted = currentProgressData.completedSteps?.includes(currentStepIndex) || false;
          
          // Check if already completed to avoid duplicate API calls
          if (!isCurrentlyCompleted) {
            
            try {
              await apiService.updateProgress(
                tutorial.id,
                step.id,
                Math.floor(timeSpent / 1000),
                true
              );
              
              // Update progress state with fresh data
              const updatedProgress = {
                ...currentProgressData,
                completedSteps: Array.from(new Set([...(currentProgressData.completedSteps || []), currentStepIndex]))
              };
              setProgress(updatedProgress);
              saveProgress(updatedProgress);
              setAutoCompleteProgress(100);
              // Stop further interval ticks once completed
              clearInterval(interval);
              
              // Force a re-render to update the checkbox state
              // The isStepCompleted will be recalculated on next render
            } catch (error) {
              console.error('Failed to auto-complete step:', error);
            }
          } else {
          }
        }
        
        // Update lastAccessed time
        const latestProgressData = JSON.parse(localStorage.getItem(`tutorial-progress-${tutorial.id}`) || 'null');
        const base = latestProgressData || progress || { tutorialId: tutorial.id, currentStepIndex: currentStepIndex, completedSteps: [] };
        saveProgress({ ...base, lastAccessed: Date.now() });
      }, 1000); // Check every second for smoother progress

      return () => clearInterval(interval);
    }
  }, [currentStepIndex]); // Only depend on currentStepIndex to avoid restarting

  const loadTutorialInLanguage = async (tutorialId: string, language: string) => {
    try {
      const response = await apiService.getTutorialInLanguage(tutorialId, language);
      if (response.success && response.data) {
        setTutorial(response.data);
      }
    } catch (error) {
      console.error('Error loading tutorial in language:', error);
    }
  };

  const handleTranslationRequested = (translationId: string) => {
    setActiveTranslation(translationId);
  };

  const handleTranslationComplete = () => {
    setActiveTranslation(null);
    // Reload tutorial to get the new translation
    if (id) {
      loadTutorialInLanguage(id, currentLanguage);
    }
  };

  const handleTranslationError = (error: string) => {
    console.error('Translation error:', error);
    setActiveTranslation(null);
  };

  const loadProgress = async (tutorialId: string) => {
    try {
      // First try to load from backend
      const backendProgress = await apiService.getProgress(tutorialId);
      if (backendProgress) {
        const progress: TutorialProgress = {
          tutorialId,
          currentStepIndex: backendProgress.currentStepIndex || 0,
          completedSteps: backendProgress.completedSteps || [],
          quizScores: {}, // TODO: Load quiz scores from backend
          startTime: Date.now(),
          lastAccessed: Date.now()
        };
        setProgress(progress);
        if (!hasUserNavigated) {
          setCurrentStepIndex(progress.currentStepIndex);
        }
        // Also save to localStorage for offline access
        saveProgress(progress);
        return;
      }
    } catch (error) {
      // Fallback to localStorage if backend fails
    }

    // Fallback to localStorage
    const savedProgress = localStorage.getItem(`tutorial-progress-${tutorialId}`);
    if (savedProgress) {
      const parsedProgress = JSON.parse(savedProgress);
      setProgress(parsedProgress);
      // Only set currentStepIndex if user hasn't navigated yet
      if (!hasUserNavigated) {
        setCurrentStepIndex(parsedProgress.currentStepIndex);
      }
    } else {
      const newProgress: TutorialProgress = {
        tutorialId,
        currentStepIndex: 0,
        completedSteps: [],
        quizScores: {},
        startTime: Date.now(),
        lastAccessed: Date.now()
      };
      setProgress(newProgress);
      if (!hasUserNavigated) {
        setCurrentStepIndex(0);
      }
      saveProgress(newProgress);
    }
  };

  const saveProgress = (progressData: TutorialProgress) => {
    const updatedProgress = {
      ...progressData,
      lastAccessed: Date.now()
    };
    localStorage.setItem(`tutorial-progress-${progressData.tutorialId}`, JSON.stringify(updatedProgress));
    setProgress(updatedProgress);
  };

  const markStepComplete = async (stepIndex: number) => {
    if (!progress || !tutorial) return;

    const isCurrentlyCompleted = progress.completedSteps.includes(stepIndex);
    const step = tutorial.steps[stepIndex];
    
    const updatedProgress = {
      ...progress,
      completedSteps: isCurrentlyCompleted
        ? progress.completedSteps.filter(i => i !== stepIndex)
        : [...progress.completedSteps, stepIndex]
    };
    
    // Update local state immediately
    setProgress(updatedProgress);
    
    // Save to localStorage
    saveProgress(updatedProgress);
    
    // Save to backend API
    try {
      await apiService.updateProgress(
        tutorial.id,
        step.id,
        0, // timeSpent - could be calculated based on how long user spent on step
        !isCurrentlyCompleted // isCompleted
      );
    } catch (error) {
      console.error('Failed to update progress on server:', error);
      // Don't revert the local state, just log the error
    }
  };

  const scrollToStep = (stepId: string) => {
    const element = document.getElementById(`step-${stepId}`);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Add a small offset for header if needed
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const navigateToStep = async (stepIndex: number, autoPlay: boolean = false) => {
    if (!tutorial || stepIndex < 0 || stepIndex >= tutorial.steps.length) {
      return;
    }

    // In continuous scroll mode, scroll to the step instead of changing index
    if (tutorial.layout === 'continuous') {
      const step = tutorial.steps[stepIndex];
      if (step) {
        scrollToStep(step.id);
        // Still update current step index for highlighting
        setCurrentStepIndex(stepIndex);
      }
      return;
    }

    // Use freshest progress but do NOT auto-complete here; timer/checkbox already handle completion
    const stored = localStorage.getItem(`tutorial-progress-${tutorial.id}`);
    const latestProgress = stored ? JSON.parse(stored) : progress;

    setCurrentStepIndex(stepIndex);
    setHasUserNavigated(true);
    setShouldAutoPlay(autoPlay);

    const baseProgress = latestProgress || progress;
    if (baseProgress) {
      const updatedProgress = {
        ...baseProgress,
        currentStepIndex: stepIndex,
        completedSteps: Array.from(new Set([...(baseProgress.completedSteps || [])]))
      };
      setProgress(updatedProgress);
      saveProgress(updatedProgress);
    }
  };


  const getTimeRemaining = () => {
    if (!tutorial) return '0 min';
    const totalTime = parseInt(tutorial.estimatedTime) || 0;
    const completedSteps = progress?.completedSteps.length || 0;
    const totalSteps = tutorial.steps.length;
    const remainingSteps = totalSteps - completedSteps;
    const timePerStep = totalTime / totalSteps;
    const remainingTime = Math.ceil(remainingSteps * timePerStep);
    return `${remainingTime} min`;
  };

  const getProgressPercentage = () => {
    if (!tutorial || !progress) return 0;
    return Math.round((progress.completedSteps.length / tutorial.steps.length) * 100);
  };

  const handleQuizAnswer = (stepId: string, questionIndex: number, answerIndex: number) => {
    if (!quizStates[stepId]) {
      setQuizStates(prev => ({
        ...prev,
        [stepId]: {
          currentQuestion: 0,
          answers: [],
          score: 0,
          isCompleted: false
        }
      }));
    }

    const quizState = quizStates[stepId] || {
      currentQuestion: 0,
      answers: [],
      score: 0,
      isCompleted: false
    };

    const newAnswers = [...quizState.answers];
    newAnswers[questionIndex] = answerIndex;
    
    setQuizStates(prev => ({
      ...prev,
      [stepId]: {
        ...quizState,
        answers: newAnswers
      }
    }));
  };

  const submitQuiz = (stepId: string) => {
    const quizState = quizStates[stepId];
    if (!quizState || !tutorial) return;

    const step = tutorial.steps.find(s => s.id === stepId);
    if (!step || step.type !== 'quiz') return;

    let score = 0;
    step.questions?.forEach((question, index) => {
      if (quizState.answers[index] === question.correctAnswer) {
        score++;
      }
    });

    const finalScore = Math.round((score / (step.questions?.length || 1)) * 100);
    
    setQuizStates(prev => ({
      ...prev,
      [stepId]: {
        ...quizState,
        score: finalScore,
        isCompleted: true
      }
    }));

    if (progress) {
      const updatedProgress = {
        ...progress,
        quizScores: {
          ...progress.quizScores,
          [stepId]: finalScore
        }
      };
      saveProgress(updatedProgress);
    }
  };

  const renderStepContent = (step: TutorialStep) => {
    switch (step.type) {
      case 'text':
        return (
          <div className="space-y-6">
            <HighlightableText
              html={renderMarkdown(step.content)}
              className="prose prose-lg max-w-none"
              highlightingEnabled={isHighlightingEnabled}
            />
          </div>
        );

      case 'video':
        return (
          <div className="space-y-4">
            {step.videoUrl && (
              (() => {
                // Check if it's a SharePoint URL, Graph API URL, or proxy URL
                const isSharePoint = step.videoUrl.includes('sharepoint.com');
                const isGraphApi = step.videoUrl.includes('graph.microsoft.com');
                const isProxyUrl = step.videoUrl.includes('/api/ai-assistant/sharepoint/video/');
                
                if (isSharePoint || isGraphApi || isProxyUrl) {
                  // Use HTML5 video tag for proxy URLs or direct video URLs
                  if (isProxyUrl || isGraphApi || step.videoUrl.includes('/content') || step.videoUrl.includes('downloadUrl')) {
                    return (
                      <div className="relative w-full bg-gray-100 rounded-lg overflow-hidden">
                        <video 
                          controls 
                          className="w-full h-auto max-h-[600px]"
                          style={{ maxWidth: '100%' }}
                        >
                          <source src={step.videoUrl} type="video/mp4" />
                          Your browser does not support the video tag. 
                          <a href={step.videoUrl.includes('sharepoint.com') ? step.videoUrl : '#'} target="_blank" rel="noopener noreferrer">
                            Watch on SharePoint
                          </a>
                        </video>
                        {(step.videoUrl?.includes('sharepoint.com') || step.metadata?.sharePointUrl) && (
                          <div className="mt-2 text-center">
                            <a
                              href={step.metadata?.sharePointUrl || step.videoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800"
                            >
                              <span>Open in SharePoint</span>
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                        )}
                      </div>
                    );
                  } else {
                    // SharePoint webUrl - show a link button instead
                    return (
                      <div className="relative w-full h-96 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg overflow-hidden border-2 border-blue-200 flex items-center justify-center">
                        <div className="text-center space-y-4 p-8">
                          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                            <svg className="w-10 h-10 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                          <div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-2">Video Available on SharePoint</h3>
                            <p className="text-gray-600 mb-4">This video is stored on SharePoint. Click below to watch it.</p>
                            <a
                              href={step.videoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span>Watch Video on SharePoint</span>
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                        </div>
                      </div>
                    );
                  }
                } else {
                  // Check if it's a SharePoint webUrl (should not be embedded in iframe)
                  const isSharePointWebUrl = step.videoUrl.includes('sharepoint.com') && 
                                            !step.videoUrl.includes('/api/') &&
                                            !step.videoUrl.includes('graph.microsoft.com') &&
                                            !step.videoUrl.includes('/content') &&
                                            !step.videoUrl.includes('downloadUrl');
                  
                  if (isSharePointWebUrl) {
                    // SharePoint webUrl - try to convert to proxy URL for embedding
                    return <SharePointVideoConverter 
                      webUrl={step.videoUrl} 
                      title={step.title} 
                      sharePointUrl={step.metadata?.sharePointUrl || step.videoUrl} 
                    />;
                  }
                  
                  // Check if it's an embeddable video URL (YouTube, Vimeo, etc.)
                  const isEmbeddableVideo = step.videoUrl.includes('youtube.com') || 
                                           step.videoUrl.includes('youtu.be') ||
                                           step.videoUrl.includes('vimeo.com') ||
                                           step.videoUrl.includes('player.vimeo.com');
                  
                  if (isEmbeddableVideo) {
                    // Embeddable video URL - use iframe
                    return (
                      <div className="relative w-full h-96 bg-gray-100 rounded-lg overflow-hidden">
                        <iframe
                          src={step.videoUrl}
                          className="w-full h-full"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={step.title}
                        />
                      </div>
                    );
                  }
                  
                  // For other video URLs, try HTML5 video tag
                  return (
                    <div className="relative w-full bg-gray-100 rounded-lg overflow-hidden">
                      <video 
                        controls 
                        className="w-full h-auto max-h-[600px]"
                        style={{ maxWidth: '100%' }}
                      >
                        <source src={step.videoUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                        <a href={step.videoUrl} target="_blank" rel="noopener noreferrer">
                          Open video in new tab
                        </a>
                      </video>
                    </div>
                  );
                }
              })()
            )}
          </div>
        );

      case 'code':
        return (
          <div className="space-y-4">
            {step.content && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Code className="w-5 h-5 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">
                      CODE
                    </span>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => navigator.clipboard.writeText(step.content)}
                      className="flex items-center space-x-1 px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      <span>Copy</span>
                    </button>
                  </div>
                </div>
                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                  <pre className="text-green-400 text-sm font-mono">
                    <code>{step.content}</code>
                  </pre>
                </div>
              </div>
            )}
          </div>
        );

      case 'pdf':
        return (
          <div className="space-y-4">
            {step.pdfUrl && (
              <div className="relative w-full h-[600px] bg-gray-100 rounded-lg overflow-hidden border border-gray-300">
                <iframe
                  src={step.pdfUrl}
                  className="w-full h-full"
                  frameBorder="0"
                  title={step.title}
                />
              </div>
            )}
            {step.content && (
              <div className="space-y-6">
                <HighlightableText
                  html={renderMarkdown(step.content)}
                  className="prose prose-lg max-w-none"
                  highlightingEnabled={isHighlightingEnabled}
                />
              </div>
            )}
            {step.pdfUrl && (
              <div className="text-center">
                <a 
                  href={step.pdfUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 px-4 py-2 bg-[#667eea] text-white rounded-lg hover:bg-[#5a6fd8] transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span>Open PDF in new tab</span>
                </a>
              </div>
            )}
          </div>
        );

      case 'quiz':
        const quizState = quizStates[step.id] || {
          currentQuestion: 0,
          answers: [],
          score: 0,
          isCompleted: false
        };

        if (quizState.isCompleted) {
          return (
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Quiz Completed!</h3>
                <p className="text-4xl font-bold text-green-600 mb-2">{quizState.score}%</p>
                <p className="text-gray-600">You scored {quizState.score}% on this quiz</p>
              </div>
            </div>
          );
        }

        return (
          <div className="space-y-6">
            
            {step.questions?.map((question, questionIndex) => (
              <div key={question.id} className="border border-gray-200 rounded-lg p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">
                  Question {questionIndex + 1}: {question.question}
                </h4>
                
                <div className="space-y-3">
                  {question.options.map((option, optionIndex) => (
                    <label
                      key={optionIndex}
                      className={cn(
                        "flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors",
                        quizState.answers[questionIndex] === optionIndex
                          ? "border-[#667eea] bg-blue-50"
                          : "border-gray-200 hover:border-gray-300"
                      )}
                    >
                      <input
                        type="radio"
                        name={`question-${questionIndex}`}
                        checked={quizState.answers[questionIndex] === optionIndex}
                        onChange={() => handleQuizAnswer(step.id, questionIndex, optionIndex)}
                        className="text-[#667eea] focus:ring-[#667eea]"
                      />
                      <span className="text-gray-900">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}

            <div className="flex justify-center">
              <button
                onClick={() => submitQuiz(step.id)}
                disabled={quizState.answers.length !== step.questions?.length}
                className="px-6 py-3 bg-gradient-to-r from-[#667eea] to-[#764ba2] text-white rounded-lg hover:from-[#5a6fd8] hover:to-[#6a4190] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Quiz
              </button>
            </div>
          </div>
        );

      case 'work-instruction-step':
        return (
          <div className="space-y-6">
            {/* Main content */}
            {step.content && (
              <div className="bg-white border border-gray-300 rounded-lg p-6">
                <HighlightableText
                  html={renderMarkdown(step.content)}
                  className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:text-gray-900 prose-p:text-gray-700 prose-p:leading-relaxed prose-ul:list-disc prose-ul:pl-6 prose-ol:list-decimal prose-ol:pl-6 prose-li:my-2 prose-strong:font-bold prose-strong:text-gray-900 prose-a:text-blue-600 prose-a:underline prose-code:bg-gray-100 prose-code:px-1 prose-code:rounded prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-blockquote:border-l-4 prose-blockquote:border-gray-300 prose-blockquote:pl-4 prose-blockquote:italic"
                />
              </div>
            )}

            {/* Metadata */}
            {step.metadata && (
              <div className="space-y-4">
                {/* Materials */}
                {step.metadata.materials && step.metadata.materials.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                      <BookOpen className="w-5 h-5 mr-2" />
                      Materials Required
                    </h4>
                    <ul className="list-disc list-inside text-blue-800 space-y-1">
                      {step.metadata.materials.map((material: string, idx: number) => (
                        <li key={idx}>{material}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Tools */}
                {step.metadata.tools && step.metadata.tools.length > 0 && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                      <Info className="w-5 h-5 mr-2" />
                      Tools Required
                    </h4>
                    <ul className="list-disc list-inside text-blue-800 space-y-1">
                      {step.metadata.tools.map((tool: string, idx: number) => (
                        <li key={idx}>{tool}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Warnings */}
                {step.metadata.warnings && step.metadata.warnings.length > 0 && (
                  <div className="bg-red-50 border-2 border-red-400 rounded-lg p-4">
                    <h4 className="font-semibold text-red-900 mb-2 flex items-center">
                      <Info className="w-5 h-5 mr-2" />
                      Safety Warnings
                    </h4>
                    <ul className="list-disc list-inside text-red-800 space-y-1">
                      {step.metadata.warnings.map((warning: string, idx: number) => (
                        <li key={idx}>{warning}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Cautions */}
                {step.metadata.cautions && step.metadata.cautions.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-400 rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-900 mb-2 flex items-center">
                      <Info className="w-5 h-5 mr-2" />
                      Cautions
                    </h4>
                    <ul className="list-disc list-inside text-yellow-800 space-y-1">
                      {step.metadata.cautions.map((caution: string, idx: number) => (
                        <li key={idx}>{caution}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Prerequisites */}
                {step.metadata.prerequisites && step.metadata.prerequisites.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-900 mb-2">Prerequisites</h4>
                    <ul className="list-disc list-inside text-yellow-800 space-y-1">
                      {step.metadata.prerequisites.map((prereq: string, idx: number) => (
                        <li key={idx}>{prereq}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Location, Quantity, Duration */}
                {(step.metadata.location || step.metadata.quantity || step.metadata.duration) && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      {step.metadata.location && (
                        <div>
                          <span className="font-semibold text-gray-700">Location:</span>
                          <span className="text-gray-600 ml-2">{step.metadata.location}</span>
                        </div>
                      )}
                      {step.metadata.quantity && (
                        <div>
                          <span className="font-semibold text-gray-700">Quantity:</span>
                          <span className="text-gray-600 ml-2">{step.metadata.quantity}</span>
                        </div>
                      )}
                      {step.metadata.duration && (
                        <div>
                          <span className="font-semibold text-gray-700">Duration:</span>
                          <span className="text-gray-600 ml-2">{step.metadata.duration}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* References */}
                {step.metadata.references && step.metadata.references.length > 0 && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <h4 className="font-semibold text-indigo-900 mb-2">References</h4>
                    <ul className="space-y-2">
                      {step.metadata.references.map((ref: any, idx: number) => (
                        <li key={idx}>
                          {ref.url ? (
                            <a
                              href={ref.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-700 hover:text-indigo-900 underline flex items-center"
                            >
                              <ExternalLink className="w-4 h-4 mr-1" />
                              {ref.title}
                            </a>
                          ) : (
                            <span className="text-indigo-800">{ref.title}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Subsections */}
            {step.subsections && step.subsections.length > 0 && (
              <div className="space-y-4">
                {step.subsections
                  .sort((a: any, b: any) => a.order - b.order)
                  .map((subsection: any) => (
                    <div key={subsection.id} className="border-l-4 border-gray-300 pl-4">
                      {subsection.title && (
                        <h5 className="font-semibold text-gray-900 mb-2">{subsection.title}</h5>
                      )}
                      <HighlightableText
                        html={renderMarkdown(subsection.content)}
                        className="prose prose-sm max-w-none text-gray-700"
                        highlightingEnabled={isHighlightingEnabled}
                      />
                    </div>
                  ))}
              </div>
            )}
          </div>
        );

      case 'safety-warning':
        return (
          <div className="space-y-6">
            <div className="bg-red-50 border-2 border-red-400 rounded-lg p-6">
              <div className="flex items-start space-x-3">
                <Info className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h4 className="font-semibold text-red-900 mb-2">Safety Warning</h4>
                  {step.content && (
                    <HighlightableText
                      html={renderMarkdown(step.content)}
                      className="prose prose-lg max-w-none text-red-900"
                      highlightingEnabled={isHighlightingEnabled}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        );

      case 'material-list':
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h4 className="font-semibold text-blue-900 mb-4 flex items-center">
                <BookOpen className="w-5 h-5 mr-2" />
                Materials Required
              </h4>
              {step.content && (
                <HighlightableText
                  html={renderMarkdown(step.content)}
                  className="prose prose-lg max-w-none text-blue-800"
                  highlightingEnabled={isHighlightingEnabled}
                />
              )}
              {step.metadata?.materials && step.metadata.materials.length > 0 && (
                <ul className="list-disc list-inside text-blue-800 space-y-1 mt-4">
                  {step.metadata.materials.map((material: string, idx: number) => (
                    <li key={idx}>{material}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-6">
            <div className="bg-yellow-50 border border-yellow-400 rounded-lg p-4">
              <p className="text-yellow-800">
                <strong>Note:</strong> This step type ({step.type}) is not yet fully supported in the viewer.
              </p>
            </div>
            {step.content && (
              <HighlightableText
                html={renderMarkdown(step.content)}
                className="prose prose-lg max-w-none"
                highlightingEnabled={isHighlightingEnabled}
              />
            )}
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#667eea] mx-auto mb-4"></div>
          <p className="text-gray-600">{t('message.loadingTutorials')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">{t('common.error')} Loading Tutorial</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/tutorials')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Tutorials
          </button>
        </div>
      </div>
    );
  }

  if (!tutorial || !progress) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">{t('message.noTutorials')}</h2>
          <p className="text-gray-600 mb-4">The tutorial you're looking for doesn't exist.</p>
          <button 
            onClick={() => navigate('/tutorials')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Tutorials
          </button>
        </div>
      </div>
    );
  }

  // Handle empty or out-of-range step index safely
  if (!tutorial.steps || tutorial.steps.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">This course has no steps yet</h2>
          <button 
            onClick={() => navigate('/tutorials')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Tutorials
          </button>
        </div>
      </div>
    );
  }

  const effectiveStepIndex = Math.min(Math.max(currentStepIndex, 0), tutorial.steps.length - 1);
  const currentStep = tutorial.steps[effectiveStepIndex];
  const isStepCompleted = progress.completedSteps.includes(effectiveStepIndex);
  const progressPercentage = getProgressPercentage();

  return (
    <div className={`min-h-screen bg-gray-50 ${isFullscreen ? 'fixed inset-0 z-50' : ''}`}>
      {/* Header - Hidden in fullscreen */}
      {!isFullscreen && (
        <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{tutorial.title}</h1>
              {collections.length > 0 && (
                <div className="flex items-center space-x-2 mt-2">
                  <FolderTree className="w-4 h-4 text-gray-400" />
                  <div className="flex items-center space-x-2 flex-wrap">
                    {collections.map((collection) => (
                      <button
                        key={collection.id}
                        onClick={() => navigate(`/collections/${collection.id}`)}
                        className="inline-flex items-center space-x-1 px-2 py-1 text-xs bg-primary-50 text-primary-700 rounded-full hover:bg-primary-100 transition-colors"
                      >
                        {collection.icon && <span>{collection.icon}</span>}
                        <span>{collection.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Fullscreen Toggle */}
            <button
              onClick={toggleFullscreen}
              className="p-2 rounded-lg transition-colors text-gray-600 hover:bg-gray-100"
              title={isFullscreen ? 'Exit Fullscreen (Esc)' : 'Enter Fullscreen (Ctrl+F)'}
            >
              {isFullscreen ? <Minimize className="w-5 h-5" /> : <Maximize className="w-5 h-5" />}
            </button>

            {/* Keyboard Shortcuts Info */}
            <div className="text-xs text-gray-500 hidden lg:block">
              <div>← → Navigate</div>
              <div>Ctrl+F Fullscreen</div>
            </div>
            <LanguageSwitcher 
              tutorialId={id} 
              showTranslateButton={true}
              onTranslationRequested={handleTranslationRequested}
            />
            <div className="text-right">
              <div className="text-sm text-gray-600">{t('tutorial.timeRemaining')}</div>
              <div className="font-semibold text-gray-900">{getTimeRemaining()}</div>
            </div>
            <div className="w-32">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-gray-600">{t('tutorial.progress')}</span>
                <span className="text-sm font-medium text-gray-900">{progressPercentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <motion.div
                  className="bg-gradient-to-r from-[#667eea] to-[#764ba2] h-2 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercentage}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>
          </div>
        </div>
        </header>
      )}

      <div className={`flex ${isFullscreen ? 'h-screen' : 'h-[calc(100vh-80px)]'}`}>
        {/* Left Sidebar - Show steps in fullscreen, full sidebar otherwise */}
        {isFullscreen ? (
          <div className={`bg-white border-r border-gray-200 overflow-y-auto transition-all duration-300 ${isStepsCollapsed ? 'w-12' : 'w-64'}`}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                {!isStepsCollapsed && (
                  <h3 className="font-semibold text-gray-900">{t('tutorial.steps')}</h3>
                )}
                <button
                  onClick={() => setIsStepsCollapsed(!isStepsCollapsed)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  title={isStepsCollapsed ? "Expand steps" : "Collapse steps"}
                >
                  {isStepsCollapsed ? (
                    <Menu className="w-5 h-5 text-gray-600" />
                  ) : (
                    <X className="w-5 h-5 text-gray-600" />
                  )}
                </button>
              </div>
              
              <div className="space-y-2">
                {tutorial.steps.map((step, index) => {
                  const isCompleted = progress.completedSteps.includes(index);
                  const isCurrent = index === currentStepIndex;

                  return (
                    <button
                      key={step.id}
                      onClick={() => navigateToStep(index, false)}
                      className={cn(
                        "w-full flex items-center rounded-lg text-left transition-colors",
                        isCurrent
                          ? "bg-blue-50 border border-blue-200"
                          : "hover:bg-gray-50",
                        isStepsCollapsed ? "p-3 justify-center" : "space-x-3 p-3"
                      )}
                      title={isStepsCollapsed ? step.title : undefined}
                    >
                      <div className="flex-shrink-0">
                        {isCompleted ? (
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        ) : (
                          <Circle className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                      {!isStepsCollapsed && (
                        <div className="flex-1 min-w-0">
                          <p className={cn(
                            "text-sm font-medium truncate",
                            isCurrent ? "text-blue-900" : "text-gray-900"
                          )}>
                            {step.title}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {step.type === 'text' ? 'Text' : 
                             step.type === 'video' ? 'Video' : 
                             step.type === 'pdf' ? 'PDF' : 
                             step.type === 'quiz' ? 'Quiz' :
                             step.type === 'work-instruction-step' ? 'Work Instruction' :
                             step.type === 'safety-warning' ? 'Safety Warning' :
                             step.type === 'material-list' ? 'Materials' :
                             'Code'}
                          </p>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className={`${isSidebarCollapsed ? 'w-3 overflow-visible' : 'w-64 overflow-y-auto'} bg-white border-r border-gray-200 transition-all duration-300 ease-in-out relative`}>
            {isSidebarCollapsed && (
              <button
                onClick={() => setIsSidebarCollapsed(false)}
                className="absolute top-4 right-[-12px] z-10 p-1 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50"
                title="Show Sidebar"
              >
                <Menu className="w-4 h-4 text-gray-600" />
              </button>
            )}
            {!isSidebarCollapsed && (
            <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">{t('tutorial.steps')}</h3>
              <button
                onClick={() => setIsSidebarCollapsed(true)}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                title="Hide Sidebar"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>
            
            <div className="space-y-2">
              {tutorial.steps.map((step, index) => {
                const isCompleted = progress.completedSteps.includes(index);
                const isCurrent = index === currentStepIndex;

                return (
                  <button
                    key={step.id}
                    onClick={() => navigateToStep(index)}
                    className={cn(
                      "w-full flex items-center space-x-3 p-3 rounded-lg text-left transition-colors",
                      isCurrent
                        ? "bg-blue-50 border border-blue-200"
                        : "hover:bg-gray-50"
                    )}
                  >
                    <div className="flex-shrink-0">
                      {isCompleted ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <Circle className="w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={cn(
                        "text-sm font-medium truncate",
                        isCurrent ? "text-blue-900" : "text-gray-900"
                      )}>
                        {step.title}
                      </p>
                      <p className="text-xs text-gray-500 capitalize">
                        {step.type} • Step {index + 1}
                      </p>
                    </div>
                    <Type className="w-4 h-4 text-gray-400" />
                  </button>
                );
              })}
            </div>

            </div>
          )}
          </div>
        )}

        {/* Main Content */}
        <div className={`flex-1 flex flex-col transition-all duration-300`}>
          <div className="flex-1 p-8 overflow-y-auto pb-32">
            {/* Fullscreen Controls */}
            {isFullscreen && (
              <div className="fixed top-4 right-20 md:right-24 z-50 flex items-center space-x-2">
                {/* Exit Fullscreen Button */}
                <button
                  onClick={exitFullscreen}
                  className="p-3 bg-white rounded-lg shadow-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  title="Exit Fullscreen (Esc)"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            )}
            
            {/* Continuous Scroll Layout */}
            {tutorial.layout === 'continuous' ? (
              <div className="max-w-6xl mx-auto space-y-8">
                {/* Tutorial Header */}
                <div className="mb-8">
                  <h1 className="text-4xl font-bold text-gray-900 mb-4">{tutorial.title}</h1>
                  {tutorial.description && (
                    <p className="text-lg text-gray-600 mb-6">{tutorial.description}</p>
                  )}
                </div>

                {/* All Steps in Continuous Scroll */}
                {tutorial.steps.map((step, index) => {
                  const isCompleted = progress.completedSteps.includes(index);
                  return (
                    <motion.div
                      key={step.id}
                      id={`step-${step.id}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 p-8"
                    >
                      <div className="mb-6">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className={cn(
                              "flex items-center justify-center w-10 h-10 rounded-full font-bold text-sm",
                              isCompleted 
                                ? "bg-green-100 text-green-700" 
                                : "bg-blue-100 text-blue-700"
                            )}>
                              {isCompleted ? <CheckCircle className="w-6 h-6" /> : index + 1}
                            </div>
                            <h2 className="text-2xl font-bold text-gray-900">{step.title}</h2>
                          </div>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isCompleted}
                              onChange={() => markStepComplete(index)}
                              className="w-5 h-5 text-[#667eea] focus:ring-[#667eea] rounded"
                            />
                            <span className="text-sm font-medium text-gray-700">
                              {isCompleted ? 'Completed' : 'Mark complete'}
                            </span>
                          </label>
                        </div>
                      </div>

                      <div>
                        {renderStepContent(step)}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              /* Step-by-Step Layout (Default) */
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStepIndex}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.3 }}
                  className="max-w-6xl mx-auto"
                >
                  <div className="mb-8">
                    <div className="flex items-center space-x-2 mb-2">
                      <Clock className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600">Step {effectiveStepIndex + 1} of {tutorial.steps.length}</span>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{currentStep.title}</h2>
                  </div>

                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 mb-8">
                    {renderStepContent(currentStep)}
                  </div>

                {/* Auto-complete Progress Indicator - Minimalist */}
                {!isStepCompleted && currentStep.type !== 'quiz' && currentStep.type !== 'code' && (
                  <div className="mb-4 flex items-center justify-center">
                    <div className="flex items-center space-x-2 px-3 py-1.5 bg-blue-50 rounded-full border border-blue-200">
                      {/* Compact Linear Progress */}
                      <div className="flex items-center space-x-2">
                        <div className="w-12 h-1.5 bg-blue-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-blue-500 rounded-full transition-all duration-1000 ease-out"
                            style={{ width: `${autoCompleteProgress}%` }}
                          ></div>
                        </div>
                        <span className="text-xs text-blue-700 font-medium min-w-[2rem]">
                          {autoCompleteProgress > 0 ? Math.ceil(calculatedTime - (autoCompleteProgress * calculatedTime / 100)) : calculatedTime}s
                        </span>
                        <div className="relative group">
                          <Info className="w-3 h-3 text-blue-500 cursor-help" />
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                            Auto-completes based on reading time
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Debug button removed */}


                  {/* Mark as Complete */}
                  <div className="flex items-center justify-between">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isStepCompleted}
                        onChange={() => markStepComplete(effectiveStepIndex)}
                        className="w-5 h-5 text-[#667eea] focus:ring-[#667eea] rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        {isStepCompleted ? 'Step completed ✓' : 'Mark as complete (optional)'}
                      </span>
                    </label>
                    {currentStep.type === 'quiz' && (
                      <span className="text-xs text-gray-500">
                        Quiz completion required
                      </span>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>
            )}
          </div>

        </div>

        {/* Technical Terms Sidebar - Show in both step-by-step and continuous scroll modes */}
        <TechnicalTermsSidebar
          content={
            tutorial.layout === 'continuous'
              ? tutorial.steps.map(step => {
                  let stepContent = step.content || '';
                  // Include subsections content if available
                  if (step.subsections && Array.isArray(step.subsections)) {
                    const subsectionsContent = step.subsections
                      .sort((a: any, b: any) => a.order - b.order)
                      .map((sub: any) => sub.content || '')
                      .join('\n\n');
                    stepContent = stepContent ? `${stepContent}\n\n${subsectionsContent}` : subsectionsContent;
                  }
                  return stepContent;
                }).join('\n\n')
              : (() => {
                  let content = currentStep.content || '';
                  // Include subsections content if available
                  if (currentStep.subsections && Array.isArray(currentStep.subsections)) {
                    const subsectionsContent = currentStep.subsections
                      .sort((a: any, b: any) => a.order - b.order)
                      .map((sub: any) => sub.content || '')
                      .join('\n\n');
                    content = content ? `${content}\n\n${subsectionsContent}` : subsectionsContent;
                  }
                  return content;
                })()
          }
          isOpen={isTechnicalTermsOpen}
          onToggle={() => setIsTechnicalTermsOpen(!isTechnicalTermsOpen)}
        />
      </div>

      {/* Global Audio Player - Bottom */}
      {tutorial && tutorial.steps.some(step => step.audioEnabled !== false && step.content) && (
        <div className="border-t border-gray-200 bg-white px-6 py-4">
          <GlobalAudioPlayer
            tutorial={tutorial}
            currentStepIndex={currentStepIndex}
            onStepChange={navigateToStep}
            autoPlayOnStepChange={shouldAutoPlay}
          />
        </div>
      )}

      {/* Global Audio Player - Fullscreen */}
      {isFullscreen && tutorial && tutorial.steps.some(step => step.audioEnabled !== false && step.content) && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 px-6 py-4">
          <GlobalAudioPlayer
            tutorial={tutorial}
            currentStepIndex={currentStepIndex}
            onStepChange={navigateToStep}
            autoPlayOnStepChange={shouldAutoPlay}
          />
        </div>
      )}

      {/* Translation Progress */}
      {activeTranslation && (
        <TranslationProgress
          translationId={activeTranslation}
          onComplete={handleTranslationComplete}
          onError={handleTranslationError}
        />
      )}
    </div>
  );
};

export default TutorialViewer;
